import { defineTemplate } from 'src/utils';
import { hipotecarioBeneficios1080x1440 } from './layouts/hip-beneficios-1080x1440';
import { PATH_URL } from '../_shared/data/path-url';
import { CATEGORY_OPTIONS } from '../_shared/data/category-options';
import { PAYMENT_METHOD_VARIANTS } from './data/payment-method-variants';
import { STORES_SECTION_VARIANTS } from './data/stores-section-variants';
import { STORES } from '../_shared/data/stores';
import { STORES_GRID_MAX } from '../_shared/images/stores-grid';

const THUMB = `${PATH_URL}/beneficios/thumbnails/beneficios-1080x1440.jpg`;

export const hipotecarioBeneficios = defineTemplate({
  mode: 'wysiwyg',
  id: 'hipotecario-beneficios',
  clientId: 'HIPOTECARIO',
  handlerKey: 'HIPOTECARIO',
  title: 'Beneficios',
  thumbnail: { url: THUMB, optimizedUrl: THUMB, aspectRatio: 'vertical' },
  formFields: [
    {
      key: 'title',
      label: 'Nombre del proyecto',
      type: 'input',
      placeholder: 'Nombre para identificar este asset',
      required: true
    },

    // valid-days
    // "MARTES Y MIÉRCOLES" / "MIÉRCOLES"
    {
      key: 'valid-days',
      label: 'Días de validez',
      type: 'input',
      placeholder: 'Ejemplo: Lunes a viernes',
      required: true,
      options: [
        { value: 'LUNES', label: 'Lunes' },
        { value: 'MARTES', label: 'Martes' },
        { value: 'MIÉRCOLES', label: 'Miércoles' },
        { value: 'JUEVES', label: 'Jueves' },
        { value: 'VIERNES', label: 'Viernes' },
        { value: 'SÁBADO', label: 'Sábado' },
        { value: 'DOMINGO', label: 'Domingo' }
      ]
    },

    // benefit-category
    // "Alimentos" / "Librerías"
    {
      key: 'benefit-category',
      label: 'Categoría',
      type: 'select',
      required: true,
      defaultValue: 'Supermercados',
      placeholder: 'Selecciona una categoría',
      options: CATEGORY_OPTIONS
    },

    // discount-value — imagen de descuento derivada (computeDefaults + image-replace en discount-value-image)
    {
      key: 'discount-value',
      label: 'Descuento',
      type: 'select',
      placeholder: 'Elegí el descuento',
      required: true,
      defaultValue: '25',
      options: [
        { value: '5', label: '5%' },
        { value: '10', label: '10%' },
        { value: '15', label: '15%' },
        { value: '25', label: '25%' },
        { value: '35', label: '35%' },
        { value: '50', label: '50%' },
        { value: '70', label: '70%' },
        { value: '85', label: '85%' }
      ]
    },

    // payment-method-text — dos variantes: tarjeta BH (select) o el logo "Pagando con Modo".
    {
      key: 'payment-method-text',
      label: 'Método de pago',
      type: 'polymorphic',
      required: true,
      defaultVariant: 'card',
      variants: [
        {
          key: 'card',
          label: 'Tarjeta BH',
          fields: [
            {
              key: 'value',
              label: 'Tarjeta',
              type: 'select',
              placeholder: 'Elegí la tarjeta',
              required: true,
              defaultValue: `con Tarjeta de\nDébito BH`,
              options: [
                { value: `con Tarjeta de\nDébito BH`, label: 'con Tarjeta de Débito BH' },
                { value: `con Tarjeta de\nCrédito BH`, label: 'con Tarjeta de Crédito BH' }
              ]
            }
          ]
        },
        {
          key: 'modo',
          label: 'Pagando con Modo',
          fields: []
        }
      ]
    },

    // stores-section: dos opciones. "texto" = "Comercios adheridos" + tope; "grilla" = tope + grilla.
    // monthly-cap va en ambas (cambia el top); participating-stores-text solo en "texto".
    {
      key: 'stores-section',
      label: 'Comercios',
      type: 'polymorphic',
      required: true,
      defaultVariant: 'text',
      variants: [
        {
          key: 'text',
          label: 'Comercios adheridos (texto)',
          fields: [
            {
              key: 'stores',
              label: 'Comercios adheridos',
              type: 'input',
              placeholder: 'En comercios adheridos',
              required: true,
              defaultValue: 'En comercios adheridos'
            },
            {
              key: 'monthly',
              label: 'Tope mensual',
              type: 'input',
              placeholder: 'Tope mensual: $10.000',
              required: true,
              defaultValue: 'Tope mensual: $10.000'
            }
          ]
        },
        {
          key: 'grid',
          label: 'Grilla de comercios',
          fields: [
            {
              key: 'monthly',
              label: 'Tope mensual',
              type: 'input',
              placeholder: 'Tope mensual: $10.000',
              required: true,
              defaultValue: 'Tope mensual: $10.000'
            }
          ]
        }
      ]
    },

    // stores-grid: logos de la grilla. Solo visible en modo grilla (shouldShowField).
    {
      key: 'stores-grid',
      label: 'Logos de comercios',
      type: 'image-multi-select',
      required: false,
      defaultValue: [],
      maxSelections: STORES_GRID_MAX,
      options: STORES.map(s => ({ value: s.value, label: s.label, imageUrl: s.imageUrl }))
    },

    // legal-text: el usuario solo elige las fechas; el texto legal se arma solo (computeDefaults).
    {
      key: 'legal-date-from',
      label: 'Válido desde',
      type: 'calendar',
      required: true,
      defaultValue: '2026-08-05'
    },
    {
      key: 'legal-date-to',
      label: 'Válido hasta',
      type: 'calendar',
      required: true,
      defaultValue: '2026-08-30'
    }
  ],
  canvasBinding: {
    resolutions: [
      {
        id: '1080x1440',
        label: '1080x1440',
        canvasWidth: 1080,
        canvasHeight: 1440,
        type: 'image',
        layout: hipotecarioBeneficios1080x1440
      }
    ],

    fieldBindings: [
      { kind: 'simple', fieldKey: 'valid-days', pieceId: 'valid-days', bindingType: 'text' },
      { kind: 'simple', fieldKey: 'benefit-category', pieceId: 'benefit-category', bindingType: 'text' },
      {
        kind: 'simple',
        fieldKey: 'benefit-category-image',
        pieceId: 'benefit-category-image',
        bindingType: 'image-replace'
      },
      {
        kind: 'simple',
        fieldKey: 'discount-value-image',
        pieceId: 'discount-value-image',
        bindingType: 'image-replace'
      },
      {
        kind: 'polymorphic',
        fieldKey: 'stores-section',
        defaultVariant: 'text',
        variants: STORES_SECTION_VARIANTS
      },
      { kind: 'simple', fieldKey: 'stores-grid', pieceId: 'stores-grid', bindingType: 'group-collection' },
      {
        kind: 'polymorphic',
        fieldKey: 'payment-method-text',
        defaultVariant: 'card',
        variants: PAYMENT_METHOD_VARIANTS
      },
      { kind: 'simple', fieldKey: 'legal-text', pieceId: 'legal-text', bindingType: 'text' }
    ]
  }
});
